import React, { useEffect, useRef, useState, forwardRef } from "react";
import { Input, type InputProps } from "./Input";

interface DebouncedInputProps extends Omit<InputProps, "value" | "onChange"> {
  id: string; // The id of the input
  value?: string; // The value of the input
  onChange: (value: string) => void; // The function to call when the value changes
  debounce?: number; // The time to wait before calling the onChange function in milliseconds
}

const DebouncedInput = forwardRef<HTMLInputElement, DebouncedInputProps>(({ id, value, onChange, debounce = 250, ...props }, ref) => {
  const [valueLoc, setValueLoc] = useState<string>(value ?? "");
  // Latest onChange, read at fire time so parent re-renders (new onChange identity) can't cancel a pending debounce.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  // The last value we emitted via onChange (or last synced from the prop). Used to
  // (a) skip emitting values the parent already has, and (b) tell an external prop
  // change apart from the echo of our own emit, so prop-sync never clobbers typing.
  const lastEmittedRef = useRef<string>(value ?? "");
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Sync the local value from the controlled prop when it changes externally (e.g. the parent clears the filter).
  useEffect(() => {
    if (value === undefined) return;
    if (value === lastEmittedRef.current) return; // echo of our own emit, or unchanged — don't fight in-progress typing
    lastEmittedRef.current = value;
    setValueLoc(value);
    // The external write wins over any pending local emit.
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
  }, [value]);

  // Debounce local edits into onChange. Keyed on valueLoc (+debounce) only.
  useEffect(() => {
    if (valueLoc === lastEmittedRef.current) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = undefined;
      lastEmittedRef.current = valueLoc;
      onChangeRef.current(valueLoc);
    }, debounce);
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = undefined;
      }
    };
  }, [valueLoc, debounce]);

  return <Input id={id} ref={ref} value={valueLoc} onChange={(e) => setValueLoc(e.target.value)} {...props} />;
});

DebouncedInput.displayName = "DebouncedInput";

export { DebouncedInput };
